import type { ColDef, ColGroupDef } from "ag-grid-community";
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "@/constants/grid/agGrid";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import {
	createRiskColumnDefs,
	createScorePillParamsFromStatus,
	formatPrivateLabel,
	formatScoreValue,
	safeFormatScoreDate,
} from "./Helpers";
import PillCellRenderer from "./Pill";
import YearFilter from "./YearFilter";

export const SupplyChainColumnDefinitions: (
	| ColDef<SearchGridSchema>
	| ColGroupDef<SearchGridSchema>
)[] = [
	{
		field: "countryRegion",
		headerName: "Selling\nCountry/\nRegion",
		minWidth: 150,
	},
	{
		field: "deptNumber",
		headerName: "Dept",
		minWidth: 100,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
	},
	{
		field: "deptName",
		headerName: "Department Name",
		minWidth: 140,
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "vendorNumber",
		headerName: "Vendor #",
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		minWidth: 120,
	},
	{
		field: "vendorNameEnglish",
		headerName: "Vendor Name\n(English)",
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "vendorNameNative",
		headerName: "Vendor Name\n(Native)",
		initialHide: true,
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "privateLabel",
		headerName: "Private\nLabel",
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		valueFormatter: formatPrivateLabel,
	},
	{
		field: "category1Description",
		headerName: "Category\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "category12Description",
		headerName: "Category12\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "category123Description",
		headerName: "Category123\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "itemNumber",
		headerName: "Item #",
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		minWidth: 120,
	},
	{
		field: "itemDescription",
		headerName: "Item\nDescription",
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 150,
	},
	{
		field: "itemDescription2",
		headerName: "Item\nDescription2",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 150,
	},
	{
		field: "commodity",
		headerName: "Commodity",
		minWidth: 150,
		filter: false,
	},
	// Inject risk-related columns from helper factory
	...createRiskColumnDefs(),
	{
		field: "transparencyScore",
		headerName: "Transparency\nScore",
		minWidth: 180,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("transparencyRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "environmentalRiskScore",
		headerName: "Environmental\nScore",
		minWidth: 160,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("environmentalRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "humanRightsRiskScore",
		headerName: "Human Rights\nScore",
		minWidth: 150,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("humanRightsRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "scmIDNum",
		headerName: "SCM ID",
		minWidth: 120,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
	},
	{
		field: "mapVersion",
		headerName: "Map Version #",
		headerTooltip: "SCM Map Version",
		minWidth: 160,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
	},
	{
		field: "scoreDate",
		headerName: "Score Date",
		minWidth: 150,
		sortable: true,
		// Display full date to users formatted as MM/DD/YYYY
		valueFormatter: safeFormatScoreDate,

		// Use custom YearFilter (registered in DefaultGrid.frameworkComponents)
		// for strict 4-digit year input and custom UI behavior
		filter: YearFilter,

		// Map the row value (timestamp) to a year for the filtering engine
		filterValueGetter: (params) => {
			// If data or value missing, return null
			if (!params.data?.scoreDate) return null;
			const d = new Date(params.data.scoreDate);
			// Return numeric year, e.g. 2026
			return Number.isNaN(d.getTime()) ? null : d.getFullYear();
		},

		// Params for YearFilter component. applyButton false enables auto-apply behavior
		filterParams: {
			defaultOption: "equals",
			applyButton: false,
			debounceMs: 300, // Add debounce to avoid rapid filter changes while typing
			filterOptions: ["equals", "notEqual", "lessThan", "greaterThan", "inRange"],
		},
	},
];
